/**
 * dataTree — the widget's PIPELINE with the user's real data flowing through
 * it, as one tree.
 *
 * The first cut of this drew two disconnected things: the stored rows at the
 * top, the selector list at the bottom. You could see "6 rows → 3 items" and
 * you could see `shelves = group medsStatus by category`, but nothing said
 * WHICH three items, or that those three are what the shelves are built from.
 *
 * So the pipeline is the spine and the data hangs off it. Every selector is
 * evaluated with `buildRenderContext` — the SAME call the runtime makes to
 * render the page, so what the tree shows is by construction what the widget
 * shows — and its real output becomes its children:
 *
 *   medicine                          6 stored rows          ← the kind
 *     meds = fold medicine            6 rows → 3 items       ← dedup, explicit
 *       Paracetamol 500 …                                    ← the folded item
 *         brand: Panadol …                                   ← its attributes
 *     medsStatus = derive expiryStatus ⟵ meds   3 items
 *     shelves = group medsStatus by category    3 buckets
 *       Fever & pain (2) → items → attributes
 *
 * Selectors are ordered by DEPENDENCY (a selector never appears above what it
 * reads) and indented by how far they sit from the kind. No React.
 *
 * ONE thing here reads the DOM, and it has to: `dataTarget` (and the
 * `renderersFor` fan-out built on it) asks the RENDER which identity a grid
 * keyed its cards by. That is not derivable from the blueprint — the two
 * collection kinds key by different fields, and which rows survive to which
 * grid is what the render just decided.
 */
import { buildRenderContext } from '../viewir/context';
import { normalizeEntry } from '../viewir/fold';
import {
  boundItemBinding, isLayoutNode, pageFocusBinding, pageOfNode,
  selectorLineage, selectorRefs, selectorsRead,
} from './focusModel';

/**
 * Row plumbing, not payload: identity, provenance and the flags `normalizeEntry`
 * synthesises. They repeat on every item and say nothing about the thing itself,
 * so the attribute list drops them (`images` is re-added as one count token).
 */
const NOISE = new Set([
  'entry_id', 'id', 'kind', 'instance_id', 'widget_id', 'log_card_id',
  'source_session_id', 'session_deleted', 'has_session', 'images', 'is_sample',
]);

/** What a human looks for first; everything else falls in alphabetically. */
const PRIORITY = ['name', 'title', 'label', 'type', 'category', 'brand', 'created_at'];

// `/api/media/…` is how a stored image reads back, so a bare PATH counts as a
// media reference too — printing the URL is noise at this size either way.
const MEDIA = /^(https?:|data:|media:|gs:|blob:|\/)/i;

const truncate = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** A media reference as a short token — the URL itself is never readable here. */
function mediaToken(s) {
  if (s.startsWith('data:')) return 'inline';
  const tail = String(s).split('?')[0].split('/').filter(Boolean).at(-1) || 'ref';
  return truncate(tail, 22);
}

/** One attribute value, short enough to sit on a single line. */
export function formatValue(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) {
    if (!v.length) return '[]';
    const inner = v.map((x) => (x && typeof x === 'object' ? '{…}' : String(x))).join(', ');
    return truncate(`[${v.length}] ${inner}`, 64);
  }
  if (typeof v === 'object') return truncate(JSON.stringify(v), 64);
  const s = String(v);
  if (MEDIA.test(s)) return `⧉ ${mediaToken(s)}`;
  return truncate(s, 64);
}

/** The meaningful payload fields of one row, ordered for reading. */
export function attrsOf(item) {
  const keys = Object.keys(item || {}).filter((k) => {
    if (NOISE.has(k)) return false;
    const v = item[k];
    return !(v === null || v === undefined || v === '' || typeof v === 'function');
  });
  keys.sort((a, b) => {
    const ia = PRIORITY.indexOf(a);
    const ib = PRIORITY.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
  const out = keys.map((k) => ({ key: k, value: formatValue(item[k]) }));
  const imgs = Array.isArray(item?.images) ? item.images.filter(Boolean) : [];
  if (imgs.length) out.push({ key: 'images', value: `⧉ ${imgs.length} image${imgs.length > 1 ? 's' : ''}` });
  return out;
}

/** The item's own name, or the least useless thing standing in for one. */
export function itemLabel(item, i) {
  return item?.name || item?.title || item?.label || item?.type
    || (item?.id ? `${String(item.id).slice(0, 8)}…` : `item ${i + 1}`);
}

/**
 * THE IDENTITIES A LAID-OUT CARD CAN BE KEYED BY — the UNION of what both
 * collection kinds use, in their own precedence order.
 *
 * A card's node id is `<gridId>__card__<key>`, and WHICH key that is depends on
 * the collection that laid it out (`viewir/components/collections.jsx`):
 *
 *   ItemCollection      `item_id || id || entry_id`
 *   SessionCollection   `grid_id || session_id || photo_url || client_ref || id`
 *
 * The tree used to carry only the first triple, so `findItemNode` matched an
 * ItemCollection card and MISSED every SessionCollection one: focusing
 * `sessionWall__card__s-ghibli` found no row, the reverse map fell back to the
 * selector, and the wire read "reads sessionWithCover" instead of connecting
 * the card to the item it renders. medicine-box only ever looked correct
 * because its cards happen to key by `item_id`.
 *
 * So the union is the answer, not a per-collection lookup: a row answers to
 * every identity it carries, whoever asks. `source_session_id` and `group_key`
 * ride along because a `group-into-one` fold writes all three of
 * `group_key` / `grid_id` / `session_id` (see `viewir/fold.js buildGroup`) and a
 * raw stored row carries only `source_session_id` — the same session, spelled
 * three ways, and a card may be keyed by any of them.
 *
 * ORDER IS LOAD-BEARING: `keys[0]` is the row's canonical id (it names the tree
 * node, and it is the first identity `dataTarget` probes a card by, so it is
 * the one a row is normally found under), so the item triple stays FIRST and nothing
 * about medicine-box moves. `photo_url` is long and never rendered — it is only
 * ever compared for equality here and in `dataTarget`.
 */
const IDENTITY_FIELDS = [
  'item_id', 'id', 'entry_id',
  'grid_id', 'session_id', 'source_session_id', 'group_key',
  'client_ref', 'photo_url',
];

/**
 * One rendered row of data. `keys` are every identity the row answers to —
 * that is what lets the explorer find the CARD that renders it, whose node id
 * is `<gridId>__card__<itemId>`.
 *
 * `state` is the LIFECYCLE the phone is drawing this row in — a row the host is
 * deleting fades on the page, so it says "deleting" here rather than looking
 * like any other item while the preview shows it going away.
 */
function toItem(row, i, deleting = null) {
  const keys = [...new Set(
    IDENTITY_FIELDS
      .map((f) => row?.[f])
      .filter((k) => k !== null && k !== undefined && k !== '')
      .map(String),
  )];
  return {
    id: `${keys[0] ?? i}#${i}`,
    keys,
    label: itemLabel(row, i),
    when: row?.created_at ? String(row.created_at).slice(0, 10) : '',
    state: deleting?.size && keys.some((k) => deleting.has(k)) ? 'deleting' : null,
    attrs: attrsOf(row),
    row,
  };
}

/** `[{id,label,items}]` is the shape every `group` selector returns. */
function isGrouped(value) {
  return Array.isArray(value) && value.length > 0
    && value.every((b) => b && typeof b === 'object' && Array.isArray(b.items));
}

/** The selectors one spec reads, by name. */
function sourcesOf(spec, names) {
  const refs = [spec?.of, spec?.from, ...Object.values(spec?.args || {})];
  const out = [];
  for (const ref of refs) {
    if (typeof ref !== 'string') continue;
    const root = ref.split('.')[0];
    if (names.has(root) && !out.includes(root)) out.push(root);
  }
  return out;
}

/**
 * The whole pipeline, evaluated against `feed` (the real one in My-data mode):
 * `{ kinds: [{ kind, rows, pending, raw }], nodes: [{ name, spec, depth,
 * sources, kind, summary, shape, groups, items, attrs }] }`.
 *
 * `kinds[].raw` is the STORED side — the feed's own entries for that kind, in
 * feed order, normalised but unfolded. The studio draws it as its own column:
 * a reader cannot tell real from derived if the only rows on screen are the
 * output of a selector.
 *
 * `shape` says what the selector produced, which is what the tree renders as
 * children: `groups` (buckets → items), `items` (rows), `object` (a derived
 * snapshot like todayTotals — its own fields), or `scalar`.
 *
 * The feed's PENDING half never reaches a selector — an in-flight capture is
 * not a stored row and there is nothing to fold — so it is reported where it
 * belongs instead: as a count on the kind, and as `state` on the rows the host
 * is deleting.
 */
export function buildPipelineTree(blueprint, feed) {
  const specs = blueprint?.data?.selectors || {};
  const names = new Set(Object.keys(specs));
  const entries = Array.isArray(feed?.entries) ? feed.entries : [];
  const ctx = buildRenderContext({ blueprint, feed: feed || {} });
  const deleting = new Set((feed?.pending?.deleting || []).map(String));
  const adds = feed?.pending?.adds || [];
  const item = (row, i) => toItem(row, i, deleting);

  const kinds = [...new Set([
    ...(blueprint?.data?.kinds || []),
    ...entries.map((e) => e?.kind).filter(Boolean),
  ])].map((kind) => {
    const rows = entries.filter((e) => e?.kind === kind);
    return {
      kind,
      rows: rows.length,
      // an add the host tagged for another kind is not this one's
      pending: adds.filter((a) => !a?.kind || a.kind === kind).length,
      // GROUND TRUTH: the stored entries themselves, normalised exactly the way
      // the fold sees them (`normalizeEntry` — payload flattened, provenance
      // attached) and NOT folded. This is the one thing the pipeline can never
      // show: two rows that fold into one item are still two rows here, which
      // is what makes "6 rows → 3 items" checkable rather than assertable.
      raw: rows.map(normalizeEntry).map(item),
    };
  });

  // Dependency order: a selector is emitted once everything it reads has been.
  // Anything left over (a cycle, or a ref the blueprint never declared) is
  // appended rather than dropped — the tree must show the whole pipeline.
  const done = new Set();
  const order = [];
  let pending = Object.keys(specs);
  while (pending.length) {
    const ready = pending.filter((n) => sourcesOf(specs[n], names).every((s) => done.has(s)));
    if (!ready.length) { order.push(...pending); break; }
    for (const n of ready) { order.push(n); done.add(n); }
    pending = pending.filter((n) => !done.has(n));
  }

  const nodes = order.map((name) => {
    const spec = specs[name] || {};
    const sources = sourcesOf(spec, names);
    // FLAT: every selector renders at the same depth — a sibling in the
    // pipeline, listed in dependency order. Its lineage shows in the `⟵ source`
    // label, NOT by nesting: nesting put a derived selector (`medCounts`) at the
    // same depth as an item row, which read as "medCounts is an item". Only a
    // selector's DATA output (items / buckets / snapshot) nests beneath it.
    const depth = 0;

    const value = ctx?.[name];
    const rootKind = (spec.op === 'fold' || spec.op === 'latest') ? spec.kind || null : null;
    const rawRows = rootKind ? entries.filter((e) => e?.kind === rootKind).length : null;

    let shape = 'scalar';
    let groups = null;
    let items = null;
    let attrs = null;
    if (isGrouped(value)) {
      shape = 'groups';
      groups = value.map((b, i) => ({
        id: String(b.id ?? i),
        label: b.label || b.id || `bucket ${i + 1}`,
        items: (b.items || []).map(item),
      }));
    } else if (Array.isArray(value)) {
      shape = 'items';
      items = value.map(item);
    } else if (value && typeof value === 'object') {
      // A snapshot (todayTotals, weekBars) is ONE object, not a one-row list:
      // its own fields ARE its children. Wrapping it as an "item 1" invented a
      // row that isn't there — and let `findItemNode` match that phantom.
      shape = 'object';
      attrs = attrsOf(value);
    }

    // The fold is where the dedup happens, so it says so IN PLACE: the raw row
    // count and the item count, side by side.
    const count = shape === 'groups' ? groups.length : (shape === 'items' ? items.length : null);
    const summary = rawRows != null && shape !== 'scalar'
      ? `${rawRows} rows → ${shape === 'object' ? 1 : count} ${shape === 'groups' ? 'buckets' : 'items'}`
      : shape === 'groups' ? `${count} buckets`
        : shape === 'items' ? `${count} items`
          : shape === 'object' ? '1 snapshot'
            : value === undefined ? 'no value' : String(value);

    return { name, spec, depth, sources, kind: rootKind, shape, groups, items, attrs, summary };
  });

  return { kinds, nodes };
}

/**
 * The selectors that read a KIND: every selector whose lineage rests on a
 * `fold`/`latest` of it. Selecting `food_entry` lights meals AND everything
 * derived from meals, down the pipeline — the mirror image of focusing a
 * module, which lights a selector and everything it rests ON.
 */
export function selectorsForKind(blueprint, kind) {
  const selectors = blueprint?.data?.selectors || {};
  const lit = new Set();
  if (!kind) return lit;
  for (const name of Object.keys(selectors)) {
    for (const id of selectorLineage(blueprint, [name])) {
      const spec = selectors[id];
      if (spec && (spec.op === 'fold' || spec.op === 'latest') && spec.kind === kind) {
        lit.add(name);
        break;
      }
    }
  }
  return lit;
}

/**
 * Find one ROW in the built tree by the identity a rendered card carries.
 *
 * The reverse of `dataTarget`: focusing `shelves__card__<itemId>` in the strip
 * has to light the item that card was rendered from. The first pipeline node
 * holding it wins — that is the fold, the earliest place the row exists as an
 * item — and `reveal` is the set of node ids that must be open for the row to
 * be on screen.
 *
 * WHOSE row, though, when two grids fold the SAME key differently? A key is not
 * always one row: scene-studio's `s-heavenly` session holds a video and a later
 * still, the wall's fold covers it with the newest result (the still) and the
 * Clips rail re-picks inside the video subset (the clip). Both cards are keyed
 * `…__card__s-heavenly`, so "the first node holding the key" answers with
 * whichever selector the tree happens to list first — and a caller that has to
 * say what tapping THAT card opens then names the other grid's row.
 *
 * `preferred` is the selector the card's own grid is bound to. Its node is
 * searched first and the scan is the fallback, so a caller that knows which
 * grid laid the card out gets that grid's row and every other caller is
 * unchanged.
 */
export function findItemNode(tree, itemId, preferred = null) {
  if (!tree || itemId == null) return null;
  const want = String(itemId);
  const inNode = (node) => {
    const base = `sel:${node.name}`;
    if (node.shape === 'groups') {
      for (const b of node.groups) {
        const hit = b.items.find((it) => it.keys.includes(want));
        if (hit) {
          return {
            node, item: hit, bucket: b,
            id: `${base}:bucket:${b.id}:item:${hit.id}`,
            reveal: [base, `${base}:bucket:${b.id}`],
          };
        }
      }
    } else if (node.items) {
      const hit = node.items.find((it) => it.keys.includes(want));
      if (hit) return { node, item: hit, bucket: null, id: `${base}:item:${hit.id}`, reveal: [base] };
    }
    return null;
  };
  const first = preferred ? tree.nodes.find((n) => n.name === preferred) : null;
  const own = first ? inNode(first) : null;
  if (own) return own;
  for (const node of tree.nodes) {
    const hit = inNode(node);
    if (hit) return hit;
  }
  return null;
}

/**
 * THE FOCUS TOKENS a page is opened with, in the HOST's own precedence — the
 * same ladder `viewir/context findFocus` and `buildFocusGrid` resolve, kept in
 * step with them so the row the tree calls "the bound item" is the row the page
 * beside it is actually rendering.
 */
const FOCUS_TOKENS = ['item_id', 'entry_id', 'id', 'focus_entry_id', 'log_card_id', 'source_session_id'];
const GRID_TOKENS = ['grid_id', 'session_id', 'photo_url'];

/** The first identity a token ladder finds in `page_args`, or null. */
function focusToken(pageArgs, tokens) {
  for (const t of tokens) {
    const v = pageArgs?.[t];
    if (v !== null && v !== undefined && v !== '') return String(v);
  }
  return null;
}

/** The first row the tree holds, whatever stage it is at — `pool[0]`'s answer. */
function firstTreeRow(tree) {
  for (const node of tree?.nodes || []) {
    if (node.shape === 'groups') {
      for (const b of node.groups) if (b.items?.length) return b.items[0];
    } else if (node.items?.length) return node.items[0];
  }
  return null;
}

/**
 * THE ROW A DETAIL PAGE IS BOUND TO, as a tree node — `findItemNode`'s answer
 * for the identity `page_args` opened the page on.
 *
 * This is the piece the studio's data model was missing. A detail page's
 * modules read `focusEntry.*`, never a named selector, so nothing connected
 * them to the data zone; the row they all render is right there in the tree,
 * addressed by an id in `page_args` rather than by a selector name.
 *
 * FAIL CLOSED both ways, exactly as the runtime does: a token that matches no
 * row returns null (`findFocus` leaves `focusEntry` null and the page draws its
 * "no item selected" state — there is nothing to wire), while NO token at all
 * falls back to the first row, because that is the row the page then renders.
 * `focusGrid` has no such fallback: a session page with no `session_id` has no
 * grid at all (`buildFocusGrid`).
 */
export function boundItemRow(tree, pageArgs = null, which = 'focusEntry') {
  if (!tree) return null;
  const grid = which === 'focusGrid';
  const key = focusToken(pageArgs, grid ? GRID_TOKENS : FOCUS_TOKENS);
  if (key != null) return findItemNode(tree, key);
  if (grid) return null;
  const first = firstTreeRow(tree);
  return first?.keys?.length ? findItemNode(tree, first.keys[0]) : null;
}

const GRID_TYPES = new Set(['ItemCollection', 'SessionCollection']);

/** A node id carries uuids and `__`; escape it before it becomes a selector. */
const esc = (id) => (globalThis.CSS?.escape ? globalThis.CSS.escape(id) : id);

/** Is this node drawn in the page the focus zone is currently rendering? */
const inFrame = (frame, id) => Boolean(frame?.querySelector(`[data-vi-node="${esc(id)}"]`));

/**
 * Where a data pick lands in ONE module: the exact CARD that renders the picked
 * item if that card is on the page (`<gridId>__card__<itemId>`, the node id
 * `collections.jsx` gives every laid-out card), otherwise the module itself.
 * Resolved against the live DOM rather than guessed, because only the render
 * knows which identity a grid keyed its cards by — and, just as load-bearing
 * for the fan-out below, whether that grid drew this row AT ALL.
 */
export function dataTarget(frame, pick, reader) {
  if (!reader) return null;
  if (!frame || !pick?.itemKeys?.length) return reader;
  for (const key of pick.itemKeys) {
    const id = `${reader}__card__${key}`;
    if (inFrame(frame, id)) return id;
  }
  return reader;
}

/**
 * THE BRANCHES A PICK FEEDS — every selector that (a) some module reads and
 * (b) rests on the picked selector, or on the kind a stored row belongs to.
 * Nothing binds `meds` or `medsStatus`; the grid binds `shelves`, which is
 * built from them, so `shelves` is the branch those rows travel down.
 *
 * Shared by the module fan-out below and by `dataEndpoints`, which needs the
 * un-narrowed set to ask each branch where its data is actually displayed.
 */
export function boundSelectors(blueprint, readers, pick) {
  if (!pick) return [];
  const specs = blueprint?.data?.selectors || {};
  const seeds = pick.selector ? [pick.selector] : [...selectorsForKind(blueprint, pick.kind)];
  if (!seeds.length) return [];
  return Object.keys(specs).filter((name) => {
    if (!(readers.get(name) || []).length) return false;
    const lineage = selectorLineage(blueprint, [name]);
    return seeds.some((s) => s === name || lineage.has(s));
  });
}

/**
 * EVERY endpoint a data pick connects to — its whole provenance in the focus
 * zone, as one array, resolved WITHOUT reference to what is focused.
 *
 * This replaced a `rendererFor` that answered with ONE module and chose it by
 * "whichever module is focused wins". A stored row is very often rendered in
 * several places at once — scene-studio's `A hug above the clouds` flows down
 * the video branch into `clipRail` AND down the session branch into
 * `sessionWall` — so that one wire moved between the two as the focus changed
 * and the same row read as changing meaning. The focus term is gone; nothing
 * here can see one.
 *
 * The result is also the ONLY resolution: the caller draws one wire per entry
 * and boxes exactly this set, so the wire's far end and the outlined card are
 * the same list read twice, never two single answers that can disagree.
 *
 * Order of the fan, and what narrows it:
 *   · seeds — the picked selector, or (a stored row / a kind) every selector
 *     resting on that kind. Endpoints are found DOWNSTREAM of those: nothing
 *     binds `meds` or `medsStatus`, the grid binds `shelves`, which is built
 *     from them.
 *   · one entry per module reading a bound selector, DEDUPED BY TARGET — the
 *     node the wire lands on. `summary` reads three of scene-studio's selectors
 *     and is still one endpoint.
 *   · a ROW pick keeps the CARDS when any resolved. The render is what knows
 *     where a row is on screen: `Harbour road in watercolour` is an image
 *     session, so the wall drew it and the clips rail did not, though both read
 *     the same fold. It is also the old "an item is LAID OUT by a grid, never
 *     COUNTED by a stat tile" rule, now stated by the render instead of guessed
 *     from types — `Paracetamol 500` feeds `medCounts` too, and the status tile
 *     is not where that medicine is. With no card anywhere (the row's page is
 *     not the one on screen) the whole set stands rather than nothing.
 *   · a module the current page does not draw is dropped: `moduleRect` falls
 *     back to the whole phone frame for a node it cannot find, so those wires
 *     would pile up on the card's left edge pointing at nothing.
 *   · grid-laying modules lead, stably, so the primary card is the first wire
 *     and the first box.
 */
export function renderersFor(blueprint, readers, pick, frame = null) {
  const bound = boundSelectors(blueprint, readers, pick);
  if (!bound.length) return [];

  const out = [];
  const seen = new Set();
  for (const selector of bound) {
    for (const module of readers.get(selector) || []) {
      if (blueprint?.components?.[module]?.type === 'host') continue;
      const target = dataTarget(frame, pick, module);
      if (!target || seen.has(target)) continue;
      seen.add(target);
      out.push({
        selector,
        module,
        target,
        label: `${pick.label} → ${target === module ? module : `${module} card`}`,
      });
    }
  }

  const cards = out.filter((e) => e.target !== e.module);
  const rows = pick.level === 'item' || pick.level === 'attr';
  const kept = rows && cards.length ? cards : out;
  const here = frame ? kept.filter((e) => inFrame(frame, e.module)) : kept;
  const isGrid = (id) => GRID_TYPES.has(blueprint?.components?.[id]?.type);
  return [...(here.length ? here : kept)]
    .sort((a, b) => Number(isGrid(b.module)) - Number(isGrid(a.module)));
}

/**
 * IS THIS ROW THE SAME THING as `item`, anywhere else in the tree?
 *
 * Two joins, and both are needed:
 *   · IDENTITY — a row answers to every id it carries (`IDENTITY_FIELDS`), so
 *     an intersection is the join. This is what connects a stored creation to
 *     the session card built from it: the raw row's `source_session_id` IS the
 *     group's `grid_id`.
 *   · THE FOLD KEY — the only thing joining two WRITES of one thing.
 *     `Ibuprofen` written twice is two rows with two row ids and one name, and
 *     `fold.key: name` is the blueprint saying the name is what makes them one
 *     item. Without it "3 rows → 2 items" stays a number nobody can click on:
 *     the superseded write shares no id with the item it became.
 *
 * A blueprint may spell the key either way (`name` or `payload.name`); the
 * tree's rows are normalised, so the payload prefix is dropped.
 */
function sameThing(item, fold) {
  const want = new Set((item?.keys || []).map(String));
  const key = String(fold?.key || '').replace(/^payload\./, '');
  const mine = key ? item?.row?.[key] : null;
  const joins = mine !== null && mine !== undefined && mine !== '';
  return (other) => other.keys.some((k) => want.has(k))
    || (joins && other.row?.[key] === mine);
}

/**
 * THE ROWS ON THE OTHER SIDE OF THE FOLD — the STORED rows that fold into
 * `item`. The direction the two columns cannot draw on their own: the left
 * column still holds both `Ibuprofen` writes while the right shows the one item
 * they became, and picking that item lights them both.
 */
export function feedersOf(tree, item, fold = null) {
  const out = new Set();
  if (!item?.keys?.length) return out;
  const same = sameThing(item, fold);
  for (const { kind, raw } of tree?.kinds || []) {
    (raw || []).forEach((row, i) => { if (same(row)) out.add(`raw:${kind}:${i}`); });
  }
  return out;
}

/**
 * …and FORWARD: every DERIVED row the same thing reaches, at every stage it
 * survives to (`sessionFold` → `sessionWithMedia` → `sessionWithCover` → the
 * bucket it lands in). The ids are the ones `DataModelCard` renders and
 * `findItemNode` returns, so a hit is directly a row on screen.
 */
export function flowOf(tree, item, fold = null) {
  const out = new Set();
  if (!item?.keys?.length) return out;
  const same = sameThing(item, fold);
  const hits = (items, base) => (items || []).forEach((it) => {
    if (same(it)) out.add(`${base}:item:${it.id}`);
  });
  for (const node of tree?.nodes || []) {
    const base = `sel:${node.name}`;
    if (node.shape === 'groups') for (const b of node.groups) hits(b.items, `${base}:bucket:${b.id}`);
    else hits(node.items, base);
  }
  return out;
}

/**
 * selector name → the modules that READ it. The EXACT inverse of
 * `selectorsRead`, and it stays exact because both are one call to
 * `selectorRefs` — the two used to carry the same scan twice, and any widening
 * of one that missed the other would put a module in the explorer's data wire
 * that the data zone's `→ module` badge did not know about.
 *
 * This is what makes the data zone bidirectional: pick a list and the explorer
 * knows which module renders it; pick an item and the card inside that module
 * is `<module>__card__<id>`.
 */
export function readersOf(blueprint) {
  const out = new Map();
  const names = new Set(Object.keys(blueprint?.data?.selectors || {}));
  for (const [id, node] of Object.entries(blueprint?.components || {})) {
    for (const sel of selectorRefs(node, names)) {
      // a bound-item marker is not a selector NAME, and this map is keyed by
      // one — see `selectorsRead`
      if (typeof sel !== 'string') continue;
      if (!out.has(sel)) out.set(sel, []);
      if (!out.get(sel).includes(id)) out.get(sel).push(id);
    }
  }
  return out;
}

/** The pipeline stage a tree row id belongs to: `sel:<name>:…` / `raw:<kind>:…`. */
export function stageOfRow(id) {
  const parts = String(id ?? '').split(':');
  return parts.length > 1 ? parts[1] : null;
}

/**
 * IS THE PICKED ROW THE PAGE'S BOUND ITEM? Two joins, the same two the tree
 * already uses everywhere else: the identities a row answers to, and — for the
 * write that shares only a fold key with the item it became — the row family
 * `feedersOf` / `flowOf` resolve.
 */
function picksBoundItem(blueprint, tree, bound, pick) {
  if (!bound || !pick) return false;
  const want = new Set((bound.item?.keys || []).map(String));
  if ((pick.itemKeys || []).some((k) => want.has(String(k)))) return true;
  const rule = blueprint?.data?.fold || null;
  const item = { keys: bound.item?.keys || [], row: bound.item?.row };
  const family = new Set([bound.id, ...feedersOf(tree, item, rule), ...flowOf(tree, item, rule)]);
  return Boolean(pick.id) && family.has(pick.id);
}

/**
 * THE MODULES A DETAIL PAGE'S BOUND ITEM DRIVES — every module on that page
 * that reads `focusEntry` / `focusGrid` / `item` (`boundItemBinding`) and is
 * actually drawn.
 *
 * Two are left out on purpose. The page ROOT is the page, not a module: its box
 * is the whole card and would swallow every other one. A pure LAYOUT node
 * (`videoGate`, a Stack that gates the clip) arranges the modules that render
 * the item and renders none of it — the same reason a tap never lands on one.
 */
function boundItemModules(blueprint, pageId, frame) {
  const page = (blueprint?.pages || []).find((p) => p?.id === pageId) || null;
  if (!page) return [];
  const out = [];
  for (const id of Object.keys(blueprint?.components || {})) {
    if (id === page.root) continue;
    if (!boundItemBinding(blueprint, id)) continue;
    if (pageOfNode(blueprint, id)?.id !== page.id) continue;
    if (isLayoutNode(blueprint, id)) continue;
    if (frame && !inFrame(frame, id)) continue;
    out.push(id);
  }
  return out;
}

/**
 * A PICK'S ENDPOINTS ON A PAGE THAT HAS A BOUND ITEM (a detail page).
 *
 * A detail page draws ONE row and lays out no collection, so the outward fan
 * had nothing to land on: every module reading a named selector belongs to
 * another page, and the nearest-displayed ladder then pointed the wires at
 * DERIVED ROWS that reach no focus zone at all. Clicking any row — including
 * rows of items this page is not showing — drew that spray of wires into the
 * data column, which is the noise this replaces.
 *
 * So a bound page resolves its own way:
 *   · the shipped fan, narrowed HARD to modules this page really draws (no
 *     "nothing is on the page, so keep everything" fallback — that fallback IS
 *     the orphaned wire);
 *   · plus, when the pick IS the row the page is bound to, the detail modules
 *     that read it — which is the connection the studio could not see;
 *   · and NOTHING otherwise. A row this page does not show connects to nothing
 *     here, so it is highlighted in the data zone and wired nowhere.
 */
function boundPageEndpoints(blueprint, readers, pick, frame, { tree, binding, pageArgs, page }) {
  const out = [];
  const seen = new Set();
  for (const e of renderersFor(blueprint, readers, pick, frame)) {
    // BELONGS here (the blueprint says so) and IS DRAWN here (the render says
    // so). The first is what the fan's own fallback gave up on when a page
    // answered with nothing — and giving up is what put wires on the index
    // page's grids while a detail page was on screen.
    if (pageOfNode(blueprint, e.module)?.id !== page) continue;
    if (frame && !inFrame(frame, e.module)) continue;
    if (seen.has(e.target)) continue;
    seen.add(e.target);
    out.push({ ...e, zone: 'focus' });
  }
  const bound = boundItemRow(tree, pageArgs, binding);
  if (picksBoundItem(blueprint, tree, bound, pick)) {
    for (const id of boundItemModules(blueprint, page, frame)) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ zone: 'focus', selector: null, module: id, target: id, label: `${pick.label} → ${id}` });
    }
  }
  return out;
}

/**
 * THE NEAREST DISPLAYED REPRESENTATION OF A PICKED ROW, one terminus per branch.
 *
 * The module fan-out above answers "which module ends up drawing this row"; it
 * cannot answer "and is the row VISIBLE there". A stat tile counts a medicine
 * and shows no medicine; an image session travels three quarters of the way
 * down the clips pipeline and is filtered out before the rail lays anything
 * out. Pointing a wire at those modules claims a display that is not there.
 *
 * So the fan lands on the highest rung of a two-rung ladder that this row
 * actually reaches:
 *   · a focus-zone CARD — `dataTarget` found `<module>__card__<key>` in the
 *     render, so the row is ON THE PHONE, and every card it drew is an endpoint
 *     (scene-studio's `A hug above the clouds` is two: the wall and the rail).
 *     A row displayed as a card is displayed; nothing upstream of that is worth
 *     a second wire, which is what `renderersFor`'s row narrowing already says.
 *   · else the last DERIVED ROW each branch carries it to that the data column
 *     is actually showing (`sel:<selector>:…:item:<id>`) — "it gets this far and
 *     no further", the honest answer for a superseded write (the fold kept the
 *     newer one's identity, so no card answers to this row) and for a row a
 *     filter drops before the grid ever sees it.
 *   · else nothing new: with no display of either kind the module fan stands,
 *     so a row whose page is not the one on screen still reads as connected.
 *
 * ONE terminus per branch on that second rung: `covered` carries the lineage of
 * every branch already terminated, so a stage that exists only so a later one
 * can (`meds` under `medsStatus`) never draws a wire beside the stage that
 * superseded it.
 *
 * Focus-INDEPENDENT, like the fan it is built on: nothing here can see a focus.
 * `hasRow(id)` is the caller's DOM probe — a row inside a collapsed node is not
 * displayed, and this must never point at one.
 *
 * `page` / `pageArgs` say WHICH page the focus zone is drawing and what it was
 * opened with. On a page bound to one row this ladder is the wrong reading
 * entirely and `boundPageEndpoints` answers instead; on an index page they
 * change nothing.
 */
export function dataEndpoints(blueprint, readers, pick, frame = null, opts = {}) {
  const { tree = null, hasRow = null, page = null, pageArgs = null } = opts;
  // A page BOUND to one row resolves its own endpoints. Everything else — the
  // entry page always, and any page whose modules read no focused row — keeps
  // the shipped fan and its nearest-displayed ladder untouched, which is what
  // "the index page is unchanged" rests on.
  const binding = pageFocusBinding(blueprint, page);
  if (binding && pick) {
    return boundPageEndpoints(blueprint, readers, pick, frame, { tree, binding, pageArgs, page });
  }
  const base = renderersFor(blueprint, readers, pick, frame).map((e) => ({ ...e, zone: 'focus' }));
  const isRow = (pick?.level === 'item' || pick?.level === 'attr') && pick?.itemKeys?.length;
  if (!isRow || !tree || !hasRow) return base;
  // the row IS on the phone — `renderersFor` already narrowed the fan to the
  // cards that drew it, and those are its nearest display
  if (base.some((e) => e.target !== e.module)) return base;

  const rule = blueprint?.data?.fold || null;
  const rows = [...flowOf(tree, { keys: pick.itemKeys, row: pick.row }, rule)];
  const rank = new Map((tree.nodes || []).map((n, i) => [n.name, i]));
  const covered = new Set();
  const cover = (name) => { for (const s of selectorLineage(blueprint, [name])) covered.add(s); };

  const cands = [];
  for (const selector of boundSelectors(blueprint, readers, pick)) {
    const lineage = selectorLineage(blueprint, [selector]);
    let best = null;
    for (const id of rows) {
      const stage = stageOfRow(id);
      if (!lineage.has(stage) || !hasRow(id)) continue;
      if (!best || rank.get(stage) > rank.get(best.stage)) best = { id, stage };
    }
    if (best) cands.push({ selector, ...best });
  }
  // nearest the display first, so the deepest stage of a branch wins its own
  // plumbing; the id breaks ties so the order never depends on object order
  cands.sort((a, b) => rank.get(b.stage) - rank.get(a.stage) || (a.id < b.id ? -1 : 1));

  const out = [];
  for (const c of cands) {
    if (covered.has(c.selector) || covered.has(c.stage)) continue;
    cover(c.selector);
    out.push({
      zone: 'derived', selector: c.stage, module: null, target: c.id,
      label: `${pick.label} → ${c.stage}`,
    });
  }
  // A row whose every stage is collapsed away resolves to nothing; the module
  // fan is still a truer answer than an empty strip.
  return out.length ? out : base;
}

/**
 * BACKWARD — the STORED writes that fold into a picked DERIVED row, as the tree
 * rows they are drawn as. A derived item is made of writes, and the join is the
 * fold key (`Ibuprofen` written twice shares a name, never an id), so this is
 * the one direction the two columns cannot show side by side.
 *
 * Only a DERIVED pick has feeders: a stored row is already the write, and
 * `feedersOf` would hand it back itself.
 */
export function feederRows(blueprint, tree, pick) {
  const isRow = (pick?.level === 'item' || pick?.level === 'attr') && pick?.itemKeys?.length;
  if (!isRow || !pick.selector || !tree) return [];
  const rule = blueprint?.data?.fold || null;
  return [...feedersOf(tree, { keys: pick.itemKeys, row: pick.row }, rule)]
    .map((id) => ({ id, stage: stageOfRow(id) }));
}

/**
 * CONVERGING — the data rows that FEED a focused thing, at ITS OWN LEVEL.
 *
 * This is the reverse of a data pick and it must not be spelled as one. Focusing
 * a card used to auto-pick that card's row and run the outward fan, so focusing
 * ONE clip drew wires from its row to every OTHER card the row surfaces in —
 * you asked about one thing and got a diagram of its siblings. A focus click
 * asks "what feeds THIS", so every wire ends on the focused node and the data
 * side is what fans.
 *
 * MATCHED HIERARCHY, which is the other half of it — a list-level datum never
 * wires to an item-level focus or the reverse. Both readings name the SAME
 * feeders and differ only in grain:
 *   · a MODULE is a list, so its feeders are lists: the KINDS its selectors
 *     ultimately fold, and the SELECTORS it reads.
 *   · a CARD is one row of that list, so its feeders are rows: the stored
 *     WRITES behind it (`feedersOf` — the same kinds, one rung down) and its
 *     row at the stages its grid actually READS, plus the fold row the tree
 *     opened onto, which is the card's own identity and is always shown.
 *
 * Reading every stage the row survives to instead — the whole of `flowOf` —
 * drew sixteen wires onto one clip, most of them plumbing nobody asked about.
 * The rows still all LIGHT: the highlight is the flow, the wires are the reads.
 */
export function feedsOfFocus(blueprint, tree, focus, found = null) {
  if (!focus || !tree) return [];
  const specs = blueprint?.data?.selectors || {};
  const reads = selectorsRead(blueprint, focus);
  if (found) {
    const rule = blueprint?.data?.fold || null;
    const item = { keys: found.item.keys, row: found.item.row };
    const wanted = new Set(reads);
    const rows = [...flowOf(tree, item, rule)]
      .filter((id) => id === found.id || wanted.has(stageOfRow(id)));
    return [...feedersOf(tree, item, rule), ...rows].map((id) => ({ id, stage: stageOfRow(id) }));
  }
  const kinds = [];
  for (const name of reads) {
    for (const s of selectorLineage(blueprint, [name])) {
      const spec = specs[s];
      const kind = (spec?.op === 'fold' || spec?.op === 'latest') ? spec.kind : null;
      if (kind && !kinds.includes(kind)) kinds.push(kind);
    }
  }
  return [
    ...kinds.map((k) => ({ id: `kind:${k}`, stage: k })),
    ...reads.map((name) => ({ id: `sel:${name}`, stage: name })),
  ];
}
